/** 14_DESIGN_SYSTEM §2. 대비는 계산으로 검증된 값이다. */
export const primitive = {
  purple: { 50:"#F4F0FF",100:"#E8E1FF",200:"#D3C6FF",300:"#A58CFF",400:"#8C72F0",500:"#7657E8",600:"#5B3FD4",700:"#4C31C4",800:"#3A2496" },
  pink:   { 50:"#FFF2F7",100:"#FFE1EC",200:"#FFB6D2",300:"#FF8FBB",400:"#FF6FAE",500:"#EE4E92",600:"#C42E6B",700:"#9E1F53" },
  yellow: { 50:"#FFFBEB",100:"#FFF0A6",200:"#FFE685",300:"#FFD95A",500:"#C79300",700:"#7A5800" },
  neutral:{ 0:"#FFFFFF",50:"#FAF9FB",100:"#F3F1F6",200:"#E7E4EC",300:"#CFC9D6",400:"#A79FB2",500:"#6B6478",700:"#3A3343",900:"#1A1420" },
  status: { success:"#1B7C51", warning:"#A2580A", danger:"#C4344B", info:"#2563C9" },
  /* 상태 배지 바탕. 각 상태색을 흰색 쪽으로 끌어온 옅은 톤이다. */
  statusSurface: { success:"#E8F3EE", warning:"#F7F0E6", danger:"#F8EDEF", info:"#EAF0FA" },
} as const;

export const semantic = {
  "bg.canvas": primitive.neutral[0],
  "bg.surface": primitive.neutral[0],
  "bg.surface-muted": primitive.neutral[100],
  "bg.oshi": primitive.pink[50],
  "bg.reward": primitive.yellow[50],
  "bg.selected": primitive.purple[50],
  "text.primary": primitive.neutral[900],
  "text.secondary": primitive.neutral[500],
  "text.on-accent": primitive.neutral[0],
  "text.on-delight": primitive.neutral[900],
  "text.link": primitive.purple[600],
  "border.subtle": primitive.neutral[200],
  "border.default": primitive.neutral[300],
  "border.focus": primitive.purple[500],
  "action.primary": primitive.purple[500],
  "action.primary-hover": primitive.purple[600],
  "action.secondary": primitive.purple[50],
  "emotion.oshi": primitive.pink[400],
  "emotion.oshi-text": primitive.pink[600],
  "delight.highlight": primitive.yellow[300],
  "delight.text": primitive.yellow[700],
  "map.marker-selected": primitive.pink[500],
  "map.cluster": primitive.purple[600],
  "map.user-location": primitive.status.info,
  /*
   * 상태 배지의 바탕. 상태를 색만으로 말하지 않으므로 (라벨이 항상 함께 있다)
   * 배경은 옅게 두되, 그 위의 글자는 AA 를 넘겨야 한다 — tokens.test 가 검사한다.
   */
  "status.success-surface": primitive.statusSurface.success,
  "status.warning-surface": primitive.statusSurface.warning,
  "status.danger-surface": primitive.statusSurface.danger,
  "status.info-surface": primitive.statusSurface.info,
} as const;

/** OD-025 확정 팔레트. 전경/배경 모두 AA 통과. */
export const categoryColors = {
  POPUP_STORE: { fg:"#935D26", bg:"#F8F3EC" },
  COLLAB_CAFE: { fg:"#9B4A3E", bg:"#F7EFED" },
  GOODS_STORE: { fg:"#2A7364", bg:"#E9F2F0" },
  GACHA_STORE: { fg:"#7E6829", bg:"#F4F2E9" },
  HOLY_PLACE:  { fg:"#2E6A8C", bg:"#E9F0F5" },
  FAN_EVENT:   { fg:"#96455E", bg:"#F7EDF0" },
  EXHIBITION:  { fg:"#556B2B", bg:"#EFF2E9" },
  CONCERT:     { fg:"#4C5B96", bg:"#EDEFF6" },
  CAFE:        { fg:"#7A5230", bg:"#F5F0EA" },
  DEFAULT:     { fg:"#5B6470", bg:"#EFF1F3" },
} as const;

/* --- WCAG 대비 계산 (테스트와 런타임 검증에 함께 쓴다) --- */
function channel(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}
export function relativeLuminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}
export function hsl(hex: string): { h: number; s: number; l: number } {
  const x = hex.replace("#", "");
  const r = parseInt(x.slice(0,2),16)/255, g = parseInt(x.slice(2,4),16)/255, b = parseInt(x.slice(4,6),16)/255;
  const mx = Math.max(r,g,b), mn = Math.min(r,g,b), l = (mx+mn)/2, d = mx-mn;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2*l - 1));
  let h = 0;
  if (d !== 0) {
    if (mx === r) h = ((g-b)/d) % 6;
    else if (mx === g) h = (b-r)/d + 2;
    else h = (r-g)/d + 4;
  }
  return { h: Math.round(((h*60)+360)%360), s: Math.round(s*100), l: Math.round(l*100) };
}

/** 브랜드 ↔ 카테고리 분리 규칙 (14 §2.7). */
export const SEPARATION = { categoryMaxL: 45, categoryMaxS: 70, brandMinL: 60, brandMinS: 76 } as const;
export const BRAND_ACCENTS = [primitive.purple[500], primitive.pink[400], primitive.pink[500], primitive.yellow[300]] as const;
